import { useState } from 'react'
import './style.scss'
import utility from 'src/utils/utility'
import { SearchField, Button, Text, Flex, Box, Card, tokens } from 'src/componentv2'
import { fontBold, fontRegular } from 'src/model/v2/interface/constants'
import { FAQCard } from 'src/componentv2/Curated/FAQCard'
import { FAQModal, FAQFormValue } from 'src/componentv2/Curated/FAQModal'
import { ConfirmModal } from 'src/componentv2/Curated/ConfirmModal'
import { FAQ_CATEGORIES } from 'src/componentv2/Curated/FAQCard/categories'

export interface FAQ {
    id: string
    category: string
    question: string
    answer: string
}

const MOCK_FAQS: FAQ[] = [
    {
        id: '1',
        category: 'Account',
        question: 'How do I open a bank account?',
        answer: 'Download the mobile app or visit any branch. You will need a valid government-issued ID, proof of address, and a minimum opening deposit.',
    },
    {
        id: '2',
        category: 'Transfers',
        question: 'How long does an inter-bank transfer take?',
        answer: 'Transfers processed via NIP are typically credited within seconds. Transfers on weekends or public holidays may see slight delays.',
    },
    {
        id: '3',
        category: 'Security',
        question: 'What do I do if I suspect unauthorized access to my account?',
        answer: 'Log in to the app and change your PIN immediately. If you cannot access your account, call our fraud hotline to block it right away.',
    },
]

const FILTERS = ['All', ...FAQ_CATEGORIES]

const SupportManagement = () => {
    const [faqs, setFaqs] = useState<FAQ[]>(MOCK_FAQS)
    const [search, setSearch] = useState('')
    const [activeCategory, setActiveCategory] = useState('All')
    const [expandedId, setExpandedId] = useState<string | null>(null)

    const [modalMode, setModalMode] = useState<'add' | 'edit' | null>(null)
    const [activeFAQ, setActiveFAQ] = useState<FAQ | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<FAQ | null>(null)

    const filteredFaqs = faqs.filter((f) => {
        const matchesSearch =
            f.question.toLowerCase().includes(search.toLowerCase()) ||
            f.answer.toLowerCase().includes(search.toLowerCase())
        const matchesCategory = activeCategory === 'All' || f.category === activeCategory
        return matchesSearch && matchesCategory
    })

    const openAdd = () => {
        setActiveFAQ(null)
        setModalMode('add')
    }

    const openEdit = (faq: FAQ) => {
        setActiveFAQ(faq)
        setModalMode('edit')
    }

    const closeModal = () => {
        setModalMode(null)
        setActiveFAQ(null)
    }

    const handleSave = (value: FAQFormValue) => {
        if (modalMode === 'edit' && activeFAQ) {
            setFaqs((prev) => prev.map((f) => (f.id === activeFAQ.id ? { ...f, ...value } : f)))
        } else {
            setFaqs((prev) => [...prev, { id: `${Date.now()}`, ...value }])
        }
        closeModal()
    }

    const handleDelete = () => {
        if (!deleteTarget) return
        setFaqs((prev) => prev.filter((f) => f.id !== deleteTarget.id))
        if (expandedId === deleteTarget.id) setExpandedId(null)
        setDeleteTarget(null)
    }

    return (
        <div className="support-management">
                <div style={{ padding: '2.4rem 3.2rem' }}>
                    <Flex justify="space-between" items="flex-start">
                        <Box>
                            <Text fontSize="2.4" fontWeight="700" color={tokens.textDark}>
                                Support Management
                            </Text>
                            <Text topMargin="0.4" fontSize="1.4" style={fontRegular} color={tokens.textMuted}>
                                Manage FAQ articles and customer support content.
                            </Text>
                        </Box>

                        <Button onClick={openAdd}>Add New FAQ</Button>
                    </Flex>

                    <Flex gap="1.6" wrap="wrap" topMargin="2.4">
                        <Card className="faq-stat-card">
                            <Text fontSize="1.3" color={tokens.textMuted}>Total FAQs</Text>
                            <Text topMargin="0.4" fontSize="2" fontWeight="700" color={tokens.primary}>
                                {faqs.length}
                            </Text>
                        </Card>
                        {FAQ_CATEGORIES.slice(0, 3).map((cat) => (
                            <Card key={cat} className="faq-stat-card">
                                <Text fontSize="1.3" color={tokens.textMuted}>{cat}</Text>
                                <Text topMargin="0.4" fontSize="2" fontWeight="700" color={tokens.textDark}>
                                    {faqs.filter((f) => f.category === cat).length}
                                </Text>
                            </Card>
                        ))}
                    </Flex>

                    <Box
                        bgColor={tokens.white}
                        style={{ marginTop: '2.4rem', borderRadius: 0 }}
                        padding="2.4"
                    >
                        <Flex justify="space-between" items="center" wrap="wrap" gap="1.6" bottomMargin="1.6">
                            <Text fontSize="1.8" style={fontBold} color={tokens.textDark}>
                                Frequently Asked Questions
                            </Text>

                            <SearchField
                                width="28rem"
                                value={search}
                                onChange={setSearch}
                                placeholder="Search FAQs"
                            />
                        </Flex>

                        <Flex gap="0.8" wrap="wrap" bottomMargin="1.6">
                            {FILTERS.map((cat) => {
                                const isActive = activeCategory === cat
                                return (
                                    <button
                                        key={cat}
                                        type="button"
                                        className={`faq-filter-pill${isActive ? ' faq-filter-pill--active' : ''}`}
                                        onClick={() => setActiveCategory(cat)}
                                    >
                                        {cat}
                                    </button>
                                )
                            })}
                        </Flex>

                        <Flex direction="column" gap="1.6" items="stretch">
                            {filteredFaqs.map((faq) => (
                                <FAQCard
                                    key={faq.id}
                                    category={faq.category}
                                    question={faq.question}
                                    answer={faq.answer}
                                    isExpanded={expandedId === faq.id}
                                    onToggleExpand={() => setExpandedId((prev) => (prev === faq.id ? null : faq.id))}
                                    onEdit={() => openEdit(faq)}
                                    onDelete={() => setDeleteTarget(faq)}
                                />
                            ))}
                        </Flex>

                        {filteredFaqs.length === 0 && (
                            <Text style={fontRegular} color={tokens.textMuted}>No FAQs found.</Text>
                        )}
                    </Box>
                </div>

            <FAQModal
                open={modalMode !== null}
                mode={modalMode ?? 'add'}
                initialValue={activeFAQ ?? undefined}
                onClose={closeModal}
                onSave={handleSave}
            />

            <ConfirmModal
                open={!!deleteTarget}
                title="Delete FAQ"
                message="This will permanently remove this FAQ. This action cannot be undone."
                confirmLabel="Delete"
                onConfirm={handleDelete}
                onClose={() => setDeleteTarget(null)}
            />
        </div>
    )
}

export default utility.routeData('support-management', 'Support Management', <SupportManagement />)